/**
 * The full system report: header counts, search and status and category
 * filters (any active filter flattens the hierarchy), the seven-column
 * table (Equipment, Status, Health, Prediction, Lifecycle, Spare parts,
 * Actions), the footer legend with Export CSV and Refresh, and the AI
 * analysis panel the previous build never mounted. Predictions and the
 * analysis go to the model only when the Worker reports an OpenAI key;
 * otherwise the MTBF fallback and the algorithmic analysis run locally.
 */
import { useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Dialog, Popover } from 'radix-ui';
import { ArrowDown, ArrowUp, Bot, Calculator, CircleCheck, Clock, Download, Eye, FileText, Info, LoaderCircle, Minus, OctagonAlert, Package, RefreshCw, Search, TriangleAlert, X } from 'lucide-react';
import { downloadBlob } from '@/features/loader/export/download';
import { Chip, IconButton, type Tone } from '@/components/ui/primitives';
import { toast } from '@/components/ui/toast';
import { cn } from '@/lib/cn';
import { clockNow, useUI } from '@/state/store';
import { findSubsystem, useTwin } from '../../store';
import { analyzeSystemReport, fetchAiAvailability, getDefaultLifecycleKnowledge, LIFECYCLE_URL, predictBatch, riskLevelOf, type AiDeps, type AiPrediction, type LifecycleKnowledge, type RiskLevel, type SystemAnalysis } from '../adapters/ai';
import { useJsonFile } from '../adapters/dataFiles';
import { useInventoryView } from '../adapters/inventory';
import { buildReportModel, csvFileName, lifetimeFor, predictionDateMs, reportCsv, type ReportPrediction, type ReportRow } from '../adapters/report';
import { findByName, findBySubsystem, useEquipmentSpecs } from '../adapters/specSheets';
import { StatusPill } from '../components/badges';
import { ConfidenceBar, LinearGauge } from '../components/bars';
import { Button, EmptyState, inputClass } from '../components/layout';
import { useAssetName } from '../hooks/useAssetName';
import { pathTo } from '../lib/cardTree';
import { fmtDate, fmtMonthYear, fmtTime, parseDateMs } from '../lib/format';
import { stockBadge } from '../lib/inventoryBadge';
import { DEFAULT_FILTERS, filterRows, filtersActive, REPORT_CATEGORIES, type ReportFilters } from '../lib/reportFilter';
import { useTwinUi } from '../state';
import { SpecSheet } from '../panels/PartsPanel';

const RISK_TONE: Record<RiskLevel, Tone> = { LOW: 'nominal', MEDIUM: 'caution', HIGH: 'warning', CRITICAL: 'warning', UNKNOWN: 'neutral' };

type Predictions = Record<string, AiPrediction | undefined>;

export function SystemReportDialog() {
  const open = useTwinUi((s) => s.dialog === 'report');
  const closeDialog = useTwinUi((s) => s.closeDialog);
  const setTab = useTwinUi((s) => s.setTab);
  const setExpanded = useTwinUi((s) => s.setExpanded);
  const select = useUI((s) => s.select);
  const element = useTwin((s) => s.element);
  const assetId = useTwin((s) => s.assetId);
  const name = useAssetName();
  const readings = useTwin((s) => s.readings);
  const history = useTwin((s) => s.history);
  const requestCamera = useTwin((s) => s.requestCamera);
  const inventory = useInventoryView();
  const specs = useEquipmentSpecs();
  const [specRow, setSpecRow] = useState<ReportRow | null>(null);
  const specTrigger = useRef<HTMLElement | null>(null);
  const specSubsystem = element && specRow ? findSubsystem(element.subsystems, specRow.id) : undefined;
  // Native-only TX parts must not inherit an RX installed instance's serial,
  // warranty or readings merely because their equipment names are similar.
  const spec = specRow
    ? findBySubsystem(specs.data, specRow.id) ?? (!specSubsystem?.modelOnly ? findByName(specs.data, specRow.name) : null)
    : null;

  const [filters, setFilters] = useState<ReportFilters>(DEFAULT_FILTERS);
  const [refreshedAt, setRefreshedAt] = useState(() => clockNow());
  const [partial, setPartial] = useState<Predictions>({});
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [analysisOpen, setAnalysisOpen] = useState(true);

  const availability = useQuery({ queryKey: ['twin', 'ai-available'], queryFn: fetchAiAvailability, staleTime: 5 * 60_000, retry: 0, enabled: open });
  const configured = availability.data === true;
  const lifecycle = useJsonFile<LifecycleKnowledge>(LIFECYCLE_URL, open && configured);

  const model = useMemo(
    () => (element ? buildReportModel(element, { readings, history, inventory: inventory.manager, nowMs: refreshedAt }) : null),
    [element, readings, history, inventory.manager, refreshedAt],
  );
  const rows = model?.rows;
  const flat = filtersActive(filters);
  const filtered = useMemo(() => (model ? filterRows(model.rows, filters) : []), [model, filters]);

  // Predictions (progressive) and the system analysis run when the dialog opens and on Refresh, once the availability check answered.
  // Keyed by the asset as well as the element: the parametric antenna twins share the rf_antenna element.
  const analysisQuery = useQuery<{ predictions: Record<string, AiPrediction>; analysis: SystemAnalysis }, Error>({
    queryKey: ['twin', 'report-analysis', assetId, element?.id, refreshedAt, configured],
    enabled: open && !!element && !!rows && rows.length > 0 && availability.isFetched,
    staleTime: Infinity,
    retry: 0,
    queryFn: async () => {
      const list = rows ?? [];
      const deps: AiDeps = {
        configured,
        lifecycle: lifecycle.data ?? (configured ? getDefaultLifecycleKnowledge() : null),
        inventory: inventory.manager,
        nowMs: clockNow,
      };
      setPartial({});
      setProgress({ done: 0, total: list.length });
      try {
        const predictions = await predictBatch(list, deps, (prediction, done, total) => {
          setPartial((prev) => ({ ...prev, [prediction.componentId]: prediction }));
          setProgress({ done, total });
        });
        const analysis = await analyzeSystemReport(element!, list, predictions, deps, name?.title);
        return { predictions, analysis };
      } finally {
        setProgress(null);
      }
    },
  });
  const predictions: Predictions = analysisQuery.data?.predictions ?? partial;
  const analysis = analysisQuery.data?.analysis ?? null;
  const analysisState: 'idle' | 'running' | 'error' = analysisQuery.isFetching || (open && availability.isPending) ? 'running' : analysisQuery.isError ? 'error' : 'idle';
  const analysisError = analysisQuery.isError ? analysisQuery.error.message : availability.isError ? `AI availability check failed: ${availability.error.message}` : null;
  const rerun = () => void analysisQuery.refetch();

  const viewIn3D = (row: ReportRow) => {
    if (!element) return;
    setExpanded(pathTo(element.subsystems, row.id).map((s) => s.id), true);
    select({ subsystemId: row.id });
    requestCamera({ subsystemId: row.id });
    setTab('health');
    closeDialog();
  };
  const openSpec = (row: ReportRow) => {
    specTrigger.current = document.activeElement as HTMLElement | null;
    setSpecRow(row);
  };
  const exportCsv = () => {
    if (!element || !model || model.rows.length === 0) {
      toast('warn', 'No data to export. Refresh the report first.');
      return;
    }
    const aiPredictions: Record<string, ReportPrediction> = {};
    for (const [id, p] of Object.entries(predictions)) if (p) aiPredictions[id] = p;
    downloadBlob(reportCsv(element, { readings, history, inventory: inventory.manager, nowMs: refreshedAt, aiPredictions }), csvFileName(clockNow()), 'text/csv;charset=utf-8;');
  };

  return (
    <Dialog.Root open={open} onOpenChange={(o) => { if (!o) { setSpecRow(null); closeDialog(); } }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm" />
        <Dialog.Content aria-describedby={undefined} className="fixed left-1/2 top-1/2 z-50 flex h-[min(900px,calc(100vh-32px))] w-[min(1280px,calc(100vw-32px))] -translate-x-1/2 -translate-y-1/2 flex-col rounded-panel border border-hairline-strong bg-raised text-xs shadow-elev-2 focus:outline-none">
          <div className="flex shrink-0 items-center justify-between gap-3 border-b border-hairline px-4 py-2">
            <Dialog.Title className="flex items-center gap-2 text-sm font-semibold text-ink">
              <FileText className="h-4 w-4 text-accent" /> System health report
              {name && <span className="font-mono text-[11px] font-normal text-ink-3">{name.title}</span>}
            </Dialog.Title>
            {model && (
              <div className="flex items-center gap-3 font-mono text-[11px]">
                <HeaderCount label="Total" value={model.counts.total} />
                <HeaderCount label="Operational" value={model.counts.operational} className="text-nominal" />
                <HeaderCount label="Warning" value={model.counts.warning} className={model.counts.warning ? 'text-caution' : undefined} />
                <HeaderCount label="Critical" value={model.counts.critical} className={model.counts.critical ? 'text-warning' : undefined} />
              </div>
            )}
            <Dialog.Close asChild>
              <IconButton label="Close (Esc)">
                <X className="h-4 w-4" />
              </IconButton>
            </Dialog.Close>
          </div>

          <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-hairline px-4 py-2">
            <label className="relative min-w-[220px] flex-1">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-3" />
              <input type="search" aria-label="Search components" placeholder="Search components or parents" value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} className={cn(inputClass, 'w-full pl-8')} />
            </label>
            <select aria-label="Status filter" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value as ReportFilters['status'] })} className={inputClass}>
              <option value="all">All statuses</option>
              <option value="critical">Critical only</option>
              <option value="warning">Warning only</option>
              <option value="operational">Operational only</option>
            </select>
            <select aria-label="Category filter" value={filters.category} onChange={(e) => setFilters({ ...filters, category: e.target.value })} className={inputClass}>
              <option value="all">All categories</option>
              {REPORT_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            {flat && (
              <button type="button" onClick={() => setFilters(DEFAULT_FILTERS)} className="text-[11px] text-ink-3 underline decoration-hairline-strong hover:text-ink">
                Clear filters
              </button>
            )}
            <span className="ml-auto inline-flex items-center gap-2 font-mono text-[11px] text-ink-3">
              {availability.isFetched && (
                <Chip tone={configured ? 'live' : 'neutral'}>
                  {configured ? <Bot className="h-3 w-3" /> : <Calculator className="h-3 w-3" />}
                  {configured ? 'AI model' : 'Algorithm only'}
                </Chip>
              )}
              {progress && (
                <span className="inline-flex items-center gap-1">
                  <LoaderCircle className="h-3.5 w-3.5 animate-spin" /> predicting {progress.done}/{progress.total}
                </span>
              )}
            </span>
          </div>

          <div className="min-h-0 flex-1 overflow-auto scrollbar-thin">
            {!model || model.rows.length === 0 ? (
              <EmptyState title="No component data." className="m-4">
                Load an element with physical components or live subsystems.
              </EmptyState>
            ) : (
              <table className="w-full border-collapse">
                <thead className="sticky top-0 z-10 bg-raised text-left text-[10px] uppercase tracking-wider text-ink-3">
                  <tr>
                    <th className="border-b border-hairline px-3 py-2 font-medium">Equipment / component</th>
                    <th className="border-b border-hairline px-3 py-2 font-medium">Status</th>
                    <th className="border-b border-hairline px-3 py-2 font-medium">Health</th>
                    <th className="border-b border-hairline px-3 py-2 font-medium">Prediction</th>
                    <th className="border-b border-hairline px-3 py-2 font-medium">Lifecycle est.</th>
                    <th className="border-b border-hairline px-3 py-2 font-medium">Spare parts</th>
                    <th className="border-b border-hairline px-3 py-2 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {flat ? (
                    filtered.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-3 py-6 text-center text-ink-3">
                          No components match the filters.
                        </td>
                      </tr>
                    ) : (
                      filtered.map((row) => <ReportTableRow key={row.id} row={row} prediction={predictions[row.id]} pending={progress !== null && !predictions[row.id]} flat onView={viewIn3D} onSpec={openSpec} />)
                    )
                  ) : (
                    model.groups.map((group) => (
                      <GroupRows key={group.id} group={group} predictions={predictions} pending={progress !== null} onView={viewIn3D} onSpec={openSpec} />
                    ))
                  )}
                </tbody>
              </table>
            )}
            {model && model.rows.length > 0 && (
              <AnalysisPanel analysis={analysis} state={analysisState} error={analysisError} open={analysisOpen} onToggle={() => setAnalysisOpen((v) => !v)} onRetry={rerun} />
            )}
          </div>

          <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-t border-hairline px-4 py-2">
            <div className="flex flex-wrap items-center gap-3 text-[10px] text-ink-3">
              <LegendItem tone="nominal" label="Operational" />
              <LegendItem tone="caution" label="Warning" />
              <LegendItem tone="warning" label="Critical" />
              <span className="inline-flex items-center gap-1">
                <Calculator className="h-3 w-3" /> Algorithm based
              </span>
              <span className="inline-flex items-center gap-1">
                <Bot className="h-3 w-3" /> AI generated
              </span>
              <span>Refreshed {fmtTime(refreshedAt)}</span>
            </div>
            <div className="flex items-center gap-1">
              <Button onClick={exportCsv}>
                <Download className="h-3.5 w-3.5" /> Export CSV
              </Button>
              <Button onClick={() => setRefreshedAt(clockNow())}>
                <RefreshCw className="h-3.5 w-3.5" /> Refresh
              </Button>
            </div>
          </div>
          <Dialog.Root open={open && specRow !== null} onOpenChange={(o) => !o && setSpecRow(null)}>
            <Dialog.Portal>
              <Dialog.Overlay className="fixed inset-0 z-[60] bg-black/65 backdrop-blur-sm" />
              <Dialog.Content
                aria-describedby={undefined}
                onCloseAutoFocus={(event) => { event.preventDefault(); specTrigger.current?.focus(); }}
                className="fixed left-1/2 top-1/2 z-[70] flex max-h-[calc(100dvh-48px)] w-[min(880px,calc(100vw-48px))] -translate-x-1/2 -translate-y-1/2 flex-col rounded-panel border border-hairline-strong bg-raised text-xs shadow-elev-2 focus:outline-none"
              >
                <div className="flex shrink-0 items-center justify-between gap-3 border-b border-hairline px-4 py-3">
                  <Dialog.Title className="text-sm font-semibold text-ink">Specification sheet — {specRow?.name}</Dialog.Title>
                  <Dialog.Close asChild><IconButton label="Close specification sheet"><X className="h-4 w-4" /></IconButton></Dialog.Close>
                </div>
                <div className="min-h-0 overflow-y-auto p-4 scrollbar-thin">
                  {specs.isPending ? <EmptyState title="Loading specification sheet…" />
                    : specs.isError ? <EmptyState title="The specification sheet could not load."><p>{specs.error.message}</p><Button onClick={() => void specs.refetch()}>Retry</Button></EmptyState>
                    : spec ? <SpecSheet key={spec.instanceId} spec={spec} inventory={inventory} subsystemId={specRow?.id ?? null} />
                    : <EmptyState title="No specification sheet is available for this component.">An installed-equipment record has not been linked to {specRow?.name}.</EmptyState>}
                </div>
                <div className="flex shrink-0 justify-end border-t border-hairline px-4 py-3">
                  <Dialog.Close asChild><Button>Back to report</Button></Dialog.Close>
                </div>
              </Dialog.Content>
            </Dialog.Portal>
          </Dialog.Root>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function HeaderCount({ label, value, className }: { label: string; value: number; className?: string }) {
  return (
    <span className="inline-flex items-baseline gap-1">
      <span className="text-[10px] uppercase tracking-wider text-ink-3">{label}</span>
      <span className={cn('text-sm text-ink', className)}>{value}</span>
    </span>
  );
}

function LegendItem({ tone, label }: { tone: Tone; label: string }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className={cn('h-2 w-2 rounded-full', tone === 'nominal' ? 'bg-nominal' : tone === 'caution' ? 'bg-caution' : 'bg-warning')} /> {label}
    </span>
  );
}

type Group = NonNullable<ReturnType<typeof buildReportModel>>['groups'][number];

function GroupRows({ group, predictions, pending, onView, onSpec }: { group: Group; predictions: Predictions; pending: boolean; onView: (r: ReportRow) => void; onSpec: (r: ReportRow) => void }) {
  return (
    <>
      <tr>
        <td colSpan={7} className="bg-inset/60 px-3 py-1 text-[10px] uppercase tracking-wider text-ink-3">
          {group.name}
        </td>
      </tr>
      {group.units.map(({ row, components }) => (
        <GroupUnit key={row.id} row={row} components={components} predictions={predictions} pending={pending} onView={onView} onSpec={onSpec} />
      ))}
    </>
  );
}

function GroupUnit({ row, components, predictions, pending, onView, onSpec }: { row: ReportRow; components: ReportRow[]; predictions: Predictions; pending: boolean; onView: (r: ReportRow) => void; onSpec: (r: ReportRow) => void }) {
  return (
    <>
      <ReportTableRow row={row} prediction={predictions[row.id]} pending={pending && !predictions[row.id]} onView={onView} onSpec={onSpec} />
      {components.map((c) => (
        <ReportTableRow key={c.id} row={c} prediction={predictions[c.id]} pending={pending && !predictions[c.id]} onView={onView} onSpec={onSpec} />
      ))}
    </>
  );
}

function ReportTableRow({ row, prediction, pending, flat, onView, onSpec }: { row: ReportRow; prediction: AiPrediction | undefined; pending: boolean; flat?: boolean; onView: (r: ReportRow) => void; onSpec: (r: ReportRow) => void }) {
  const depth = row.itemType === 'unit' ? 0 : row.itemType === 'component' ? 1 : 2;
  const lifetime = lifetimeFor(row.id, row.name);
  const badge = row.inventory ? stockBadge(row.inventory) : null;
  const lifecycle = row.lifecycle;
  const lifecycleColour = lifecycle ? (lifecycle.days > 365 ? 'text-nominal' : lifecycle.days > 180 ? 'text-caution' : 'text-warning') : 'text-ink-3';
  return (
    <tr className={cn('border-b border-hairline align-top hover:bg-overlay/50', depth === 0 && 'bg-raised')}>
      <td className="px-3 py-2" style={{ paddingLeft: 12 + (flat ? 0 : depth * 14) }}>
        <div className={cn(depth === 0 ? 'text-ink' : 'text-ink-2')}>
          {flat && row.parentLabel && <span className="text-ink-3">{row.parentLabel} » </span>}
          {row.name}
        </div>
        <div className="text-[10px] text-ink-3">
          {row.itemType}
          {row.itemType === 'unit' && row.componentCount > 0 && `, ${row.componentCount} components`}, {row.category}
        </div>
      </td>
      <td className="px-3 py-2">
        <StatusPill colour={row.colour} label={row.level} />
      </td>
      <td className="w-36 px-3 py-2">
        <LinearGauge value={row.health} />
        {row.healthTrend !== 0 && <div className="text-[10px] text-ink-3">trend {row.healthTrend > 0 ? 'worsening' : 'improving'}</div>}
      </td>
      <td className="w-52 px-3 py-2">
        <PredictionCell prediction={prediction} pending={pending} row={row} />
      </td>
      <td className="w-32 px-3 py-2">
        {lifecycle ? (
          <>
            <div className={cn('font-mono', lifecycleColour)}>{lifecycle.days > 365 ? `${Math.round(lifecycle.days / 365)} yrs` : `${lifecycle.days} days`}</div>
            <div className="text-[10px] text-ink-3">{fmtMonthYear(lifecycle.dateMs)}</div>
            <div className="text-[10px] text-ink-3">
              {lifecycle.reason}, base {lifetime.baseLifetime} y
            </div>
          </>
        ) : (
          <span className="text-ink-3">N/A</span>
        )}
      </td>
      <td className="w-32 px-3 py-2">
        {row.inventory && badge ? (
          <button type="button" onClick={() => onSpec(row)} className="text-left" title="Open the specification sheet">
            <div className="flex items-center gap-1.5">
              <span className="font-mono text-ink">{row.inventory.quantity}</span>
              <Chip tone={badge.tone} className="px-1.5 py-0 text-[9px]">
                {badge.label}
              </Chip>
            </div>
            <div className="truncate text-[10px] text-ink-3">{row.inventory.partNumber}</div>
          </button>
        ) : (
          <span className="text-ink-3">N/A</span>
        )}
      </td>
      <td className="w-20 px-3 py-2">
        <div className="flex items-center gap-1">
          <IconButton label="View in 3D" onClick={() => onView(row)} className="h-7 w-7">
            <Eye className="h-3.5 w-3.5" />
          </IconButton>
          <IconButton label="Specification sheet" onClick={() => onSpec(row)} className="h-7 w-7">
            <FileText className="h-3.5 w-3.5" />
          </IconButton>
        </div>
      </td>
    </tr>
  );
}

function urgencyOf(days: number | null): { tone: Tone; Icon: typeof CircleCheck } {
  if (days === null) return { tone: 'nominal', Icon: CircleCheck };
  if (days <= 30) return { tone: 'warning', Icon: TriangleAlert };
  if (days <= 90) return { tone: 'warning', Icon: OctagonAlert };
  if (days <= 180) return { tone: 'caution', Icon: Clock };
  return { tone: 'nominal', Icon: CircleCheck };
}

function PredictionCell({ prediction, pending, row }: { prediction: AiPrediction | undefined; pending: boolean; row: ReportRow }) {
  if (!prediction) {
    return pending ? (
      <div className="flex flex-col gap-1" aria-busy="true">
        <div className="inline-flex items-center gap-1 text-ink-3">
          <LoaderCircle className="h-3.5 w-3.5 animate-spin" /> Analyzing
        </div>
        <div className="h-2 w-3/4 animate-pulse rounded bg-overlay" />
        <div className="h-2 w-1/2 animate-pulse rounded bg-overlay" />
      </div>
    ) : (
      <span className="text-ink-3">Not analysed</span>
    );
  }
  const { tone, Icon } = urgencyOf(prediction.daysUntilFailure);
  const colour = tone === 'warning' ? 'text-warning' : tone === 'caution' ? 'text-caution' : 'text-nominal';
  const days = prediction.daysUntilFailure;
  const inventoryWarning =
    prediction.inventoryAlert ??
    (row.inventory && row.inventory.quantity === 0 && days !== null && days <= 90 ? 'No spare parts' : row.inventory && row.inventory.quantity <= (row.inventory.minStock || 1) && days !== null && days <= 180 ? 'Low stock' : null);
  const direction = prediction.trend?.direction?.toLowerCase() ?? 'stable';
  const dateMs = predictionDateMs(prediction);
  return (
    <div className="flex flex-col gap-0.5">
      <div className={cn('flex items-center gap-1.5', colour)}>
        <Icon className="h-3.5 w-3.5" />
        <span className="font-mono">{days === null ? 'No failure' : `${days} days`}</span>
        {direction === 'degrading' || direction === 'rising' || direction === 'declining' ? <ArrowUp className="h-3 w-3 text-caution" aria-label="Degrading" /> : direction === 'falling' || direction === 'improving' ? <ArrowDown className="h-3 w-3 text-nominal" aria-label="Improving" /> : <Minus className="h-3 w-3 text-ink-3" aria-label="Stable" />}
        {prediction.isAIGenerated ? <Bot className="h-3 w-3 text-accent" aria-label="AI generated" /> : <Calculator className="h-3 w-3 text-ink-3" aria-label="Algorithm based" />}
      </div>
      <div className="text-[10px] text-ink-3">{dateMs !== null ? fmtDate(dateMs) : 'Within 2 years'}</div>
      <div className="flex items-center justify-between gap-2">
        <ConfidenceBar value={prediction.confidence} />
        <JustificationPopover prediction={prediction} row={row} />
      </div>
      {inventoryWarning && (
        <div className={cn('inline-flex items-center gap-1 text-[10px]', row.inventory?.quantity === 0 ? 'text-warning' : 'text-caution')}>
          <Package className="h-3 w-3" /> {inventoryWarning}
        </div>
      )}
    </div>
  );
}

function JustificationPopover({ prediction, row }: { prediction: AiPrediction; row: ReportRow }) {
  const risk = riskLevelOf(prediction.riskLevel);
  const generatedMs = parseDateMs(prediction.generatedAt);
  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <button type="button" aria-label="Prediction analysis" title="Prediction analysis" className="rounded-control p-0.5 text-ink-3 hover:bg-overlay hover:text-accent">
          <Info className="h-3.5 w-3.5" />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content side="left" align="start" sideOffset={6} className="z-[60] w-[360px] rounded-panel border border-hairline-strong bg-raised p-3 text-xs shadow-elev-2 focus:outline-none">
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="text-sm font-semibold text-ink">{row.name}</div>
              <div className="text-[10px] text-ink-3">{row.category}</div>
            </div>
            <Chip tone={RISK_TONE[risk]}>{risk} RISK</Chip>
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            {prediction.daysUntilFailure !== null ? (
              <>
                <span className="font-mono text-2xl text-ink">{prediction.daysUntilFailure}</span>
                <span className="text-ink-3">days until predicted failure</span>
              </>
            ) : (
              <span className="inline-flex items-center gap-1 text-nominal">
                <CircleCheck className="h-3.5 w-3.5" /> No failure predicted within 2 years
              </span>
            )}
          </div>
          <div className="mt-1 flex items-center gap-2">
            <span className="text-ink-3">Confidence</span>
            <ConfidenceBar value={prediction.confidence} />
          </div>
          <Section title="Analysis justification">{prediction.justification}</Section>
          {prediction.leadIndicators.length > 0 && (
            <Section title="Lead indicators">
              <ul className="list-disc pl-4">
                {prediction.leadIndicators.map((l) => (
                  <li key={l.sensor}>
                    <strong className="text-ink">{l.sensor}:</strong> {l.concern ?? l.trend ?? l.rate ?? ''}
                  </li>
                ))}
              </ul>
            </Section>
          )}
          {prediction.failureMode && <Section title="Likely failure mode">{prediction.failureMode}</Section>}
          <Section title="Recommended action">
            {prediction.recommendedAction}
            <div className="mt-1 text-[10px] text-ink-3">Maintenance window: {prediction.maintenanceWindow}</div>
          </Section>
          {(prediction.inventoryAlert || row.inventory?.quantity === 0) && (
            <Section title="Inventory status">
              {prediction.inventoryAlert}
              {row.inventory && (
                <div className="text-[10px] text-ink-3">
                  In stock {row.inventory.quantity}, lead time {row.inventory.leadTimeDays} days
                </div>
              )}
              {row.inventory?.quantity === 0 && <div className="text-warning">Immediate procurement required.</div>}
            </Section>
          )}
          {prediction.dependencyRisk && <Section title="Dependency impact">{prediction.dependencyRisk}</Section>}
          <div className="mt-2 flex items-center gap-1 border-t border-hairline pt-2 text-[10px] text-ink-3">
            {prediction.isAIGenerated ? <Bot className="h-3 w-3" /> : <Calculator className="h-3 w-3" />}
            {prediction.isAIGenerated ? 'AI generated' : 'Algorithm based'}
            {generatedMs !== null && `, ${fmtDate(generatedMs)} ${fmtTime(generatedMs)}`}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mt-2">
      <div className="text-[10px] uppercase tracking-wider text-ink-3">{title}</div>
      <div className="leading-snug text-ink-2">{children}</div>
    </div>
  );
}

function AnalysisPanel({ analysis, state, error, open, onToggle, onRetry }: { analysis: SystemAnalysis | null; state: 'idle' | 'running' | 'error'; error: string | null; open: boolean; onToggle: () => void; onRetry: () => void }) {
  return (
    <div className="m-4 rounded-control border border-hairline bg-inset/40">
      <div className="flex items-center justify-between gap-2 px-3 py-2">
        <button type="button" onClick={onToggle} className="flex items-center gap-2 text-left text-sm font-semibold text-ink">
          {analysis?.isAIGenerated ? <Bot className="h-4 w-4 text-accent" /> : <Calculator className="h-4 w-4 text-ink-3" />}
          System analysis
          <span className="text-[10px] font-normal uppercase tracking-wider text-ink-3">{analysis ? (analysis.isAIGenerated ? 'AI generated' : 'Algorithm based') : state === 'running' ? 'running' : ''}</span>
        </button>
        <div className="flex items-center gap-1">
          {analysis && <Chip tone={RISK_TONE[analysis.riskLevel]}>{analysis.riskLevel} RISK</Chip>}
          <Button onClick={onRetry} disabled={state === 'running'} className="px-2 py-1">
            <RefreshCw className={cn('h-3 w-3', state === 'running' && 'animate-spin')} /> {state === 'error' ? 'Retry' : 'Re-run'}
          </Button>
        </div>
      </div>
      {open && (
        <div className="border-t border-hairline px-3 py-2">
          {state === 'running' && !analysis && (
            <div className="flex flex-col gap-2" aria-busy="true">
              <div className="h-3 w-1/3 animate-pulse rounded bg-overlay" />
              <div className="h-2 w-full animate-pulse rounded bg-overlay" />
              <div className="h-2 w-5/6 animate-pulse rounded bg-overlay" />
            </div>
          )}
          {state === 'error' && (
            <div className="flex items-center gap-2 text-warning">
              <TriangleAlert className="h-4 w-4" /> Analysis failed: {error}
            </div>
          )}
          {analysis && (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div className="md:col-span-2">
                <p className="leading-snug text-ink">{analysis.overallAssessment}</p>
              </div>
              {analysis.criticalIssues.length > 0 && <IssueBlock title="Critical issues" items={analysis.criticalIssues} tone="warning" />}
              {analysis.warningIssues.length > 0 && <IssueBlock title="Warning issues" items={analysis.warningIssues} tone="caution" />}
              <div>
                <div className="text-[10px] uppercase tracking-wider text-ink-3">Recommendations</div>
                <ol className="list-decimal pl-4 text-ink-2">
                  {analysis.recommendations.map((r) => (
                    <li key={r}>{r}</li>
                  ))}
                </ol>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-ink-3">Maintenance timeline</div>
                <Timeline label="Immediate (24 h)" items={analysis.maintenanceNeeds.immediate} tone="warning" />
                <Timeline label="This week" items={analysis.maintenanceNeeds.shortTerm} tone="caution" />
                <Timeline label="Scheduled" items={analysis.maintenanceNeeds.scheduled} tone="neutral" />
                {analysis.maintenanceNeeds.immediate.length + analysis.maintenanceNeeds.shortTerm.length + analysis.maintenanceNeeds.scheduled.length === 0 && <div className="text-ink-3">Nothing scheduled.</div>}
              </div>
              {analysis.inventoryActions.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-ink-3">Inventory actions</div>
                  <ul className="list-disc pl-4 text-ink-2">
                    {analysis.inventoryActions.map((a) => (
                      <li key={a}>{a}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="flex flex-wrap gap-3 text-[11px] text-ink-3 md:col-span-2">
                <span>Confidence {analysis.confidenceLevel}%</span>
                <span>Predicted downtime risk: {analysis.predictedDowntime}</span>
                <span>Generated {fmtTime(analysis.generatedAtMs)}</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function IssueBlock({ title, items, tone }: { title: string; items: string[]; tone: Tone }) {
  return (
    <div className={cn('rounded-control border px-3 py-2', tone === 'warning' ? 'border-warning/30 bg-warning/5' : 'border-caution/30 bg-caution/5')}>
      <div className={cn('text-[10px] uppercase tracking-wider', tone === 'warning' ? 'text-warning' : 'text-caution')}>{title}</div>
      <ul className="list-disc pl-4 text-ink-2">
        {items.map((i) => (
          <li key={i}>{i}</li>
        ))}
      </ul>
    </div>
  );
}

function Timeline({ label, items, tone }: { label: string; items: string[]; tone: Tone }) {
  if (items.length === 0) return null;
  return (
    <div className="mt-1 flex gap-2">
      <Chip tone={tone} className="h-fit shrink-0">
        {label}
      </Chip>
      <span className="text-ink-2">{items.join(', ')}</span>
    </div>
  );
}
